import { CookieConsent } from '@/components/gdpr/cookieConsentShared';
import { ga4Service } from '@/services/analytics/ga4Service';

const CONSENT_KEY = 'totoro_cookie_consent';

// Check for debug mode via URL parameter
export const isDebugMode = (): boolean => {
  // Disable debug mode entirely in production builds
  if (import.meta.env.PROD) return false;
  
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('debug') === 'true';
};

export const getCookieConsent = (): CookieConsent | null => {
  try {
    // In debug mode, always return granted consent
    if (isDebugMode()) {
      console.info('🐛 Debug mode active: Cookie consent automatically granted');
      return {
        necessary: true,
        analytics: true,
        marketing: true,
        timestamp: Date.now(),
        version: 1
      };
    }

    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return null;
    return JSON.parse(consent) as CookieConsent;
  } catch (error) {
    console.error('Failed to get cookie consent:', error);
    return null;
  }
};

export const hasAnalyticsConsent = (): boolean => {
  if (isDebugMode()) return true;
  const consent = getCookieConsent();
  return consent?.analytics ?? false;
};

export const hasMarketingConsent = (): boolean => {
  if (isDebugMode()) return true;
  const consent = getCookieConsent();
  return consent?.marketing ?? false;
};

// Listen for consent changes
export const onConsentChanged = (callback: (consent: CookieConsent) => void) => {
  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<CookieConsent>;
    callback(customEvent.detail);
    
    // Update GA4 consent when user changes preferences
    ga4Service.updateConsent();
  };
  
  window.addEventListener('cookieConsentChanged', handler);
  
  return () => {
    window.removeEventListener('cookieConsentChanged', handler);
  };
};
